import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  PlanningScenarioFormDto,
  PlanningScenarioFormInterface
} from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-form.dto';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { Observable } from 'rxjs';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { PlanningScenarioService } from '@zaoblako/web/core/services/budget/planning-scenario.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { PlanningScenarioFindInterface } from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { TagModule } from 'primeng/tag';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { DialogModule } from 'primeng/dialog';
import { PlanningScenarioSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/planning-scenario-single-select/planning-scenario-single-select.component';
import { PlanningScenarioDeleteOneQueryInterface } from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-delete-one-query.dto';

@Component({
  selector: 'crm-planning-scenario-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    TagModule,
    EditorComponent,
    DialogModule,
    PlanningScenarioSingleSelectComponent
  ],
  templateUrl: './planning-scenario-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningScenarioFormComponent extends BaseFormComponent<PlanningScenarioFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public planningScenario: PlanningScenarioFormInterface = <PlanningScenarioFormInterface>{};

  public dto = PlanningScenarioFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public showChangePlanningScenarioDialog = false;
  public loadingChangePlanningScenario = false;
  public selectPlanningScenario: PlanningScenarioFindInterface = null;
  public excludePlanningScenarioIds: number[] = [];

  public constructor(
    private readonly planningScenarioService: PlanningScenarioService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.planningScenarioService
      .findById<PlanningScenarioFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (planningScenario) => {
          this.planningScenario = planningScenario;
          this.excludePlanningScenarioIds = Array.from([planningScenario.id]);
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public savePlanningScenario() {
    if (this.checkValidate(this.planningScenario).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.planningScenarioService
          .update<PlanningScenarioFormInterface, PlanningScenarioFindInterface>(this.planningScenario)
          .subscribe({
            next: (planningScenario) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Сценарий "${this.planningScenario.name}" обновлен`);
              this.formStoreService.saveNext(planningScenario, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.planningScenarioService.create<PlanningScenarioFindInterface>(this.planningScenario).subscribe({
          next: (planningScenario) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Сценарий создан');
            this.formStoreService.saveNext(planningScenario, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deletePlanningScenario() {
    const budgetRequestsCount = (this.planningScenario as PlanningScenarioFindInterface).budgetRequestsCount;
    if (budgetRequestsCount) {
      this.showChangePlanningScenarioDialog = true;
      this.cdRef.markForCheck();
    } else {
      this.confirmService.confirm({
        message: `Вы действительно хотите удалить сценарий "${this.planningScenario.name}"?`,
        accept: () => {
          this.formStoreService.setLoading(true, this.index);
          this.cdRef.markForCheck();

          this.planningScenarioService
            .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
            .subscribe({
              next: () => {
                this.errorService.addSuccessMessage(`Сценарий "${this.planningScenario.name}" удален`);
                this.formStoreService.deleteNext(this.index);
              },
              error: () => {
                this.formStoreService.setLoading(false, this.index);
              }
            });
        }
      });
    }
  }

  public restorePlanningScenario() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить сценарий "${this.planningScenario.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.planningScenarioService.restoreOne(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.planningScenario = { ...this.planningScenario, deleted: false } as any;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Сценарий "${this.planningScenario.name}" восстановлен`);
            this.formStoreService.saveNext(this.planningScenario, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public delegateTo() {
    this.loadingChangePlanningScenario = true;
    this.cdRef.markForCheck();

    this.planningScenarioService
      .deleteById(this.formStoreService.getState.forms[this.index]?.entityId, <PlanningScenarioDeleteOneQueryInterface>{
        delegateTo: this.selectPlanningScenario?.id
      })
      .subscribe({
        next: () => {
          this.errorService.addSuccessMessage(`Сценарий "${this.planningScenario.name}" удален`);
          this.loadingChangePlanningScenario = false;
          this.showChangePlanningScenarioDialog = false;
          this.cdRef.markForCheck();
          this.formStoreService.deleteNext(this.index);
        },
        error: () => {
          this.loadingChangePlanningScenario = false;
          this.cdRef.markForCheck();
        }
      });
  }
}
